import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useMatchMedia } from "@/lib/useMatchMedia";
import { useHeroSplineMount } from "@/components/sections/hero/useHeroSplineVisibility";
import CompanyHeroSplineCards from "@/components/sections/company-v2/CompanyHeroSplineCards";
import "./HeroSection.css";

const MOBILE_MAX = 767;

export default function HeroSection({ onContact }) {
  const isMobile = useMatchMedia(`(max-width: ${MOBILE_MAX}px)`);
  const enableSpline = isMobile === false;
  const { wrapRef, showSpline: showCardsBase } = useHeroSplineMount(enableSpline, 1100);
  const showCards = enableSpline && showCardsBase;

  const scrollToServices = () => {
    document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={wrapRef}
      id="top"
      data-testid="hero-section"
      className="ds-company-v2-hero relative min-h-screen flex items-center overflow-hidden text-white pt-24"
    >
      {showCards ? (
        <div
          className="ds-company-v2-hero__spline-layer is-mounted"
          data-testid="company-v2-hero-spline"
        >
          <CompanyHeroSplineCards />
        </div>
      ) : null}

      <div className="ds-company-v2-hero__content relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="ds-company-v2-hero__copy max-w-4xl"
        >
          <div className="ds-company-v2-hero__badge" role="presentation">
            <span className="ds-company-v2-hero__badge-rail" aria-hidden="true" />
            <span className="ds-company-v2-hero__badge-body">
              <span data-testid="hero-tagline" className="ds-company-v2-hero__badge-text">
                DSITESOLUTION
              </span>
              <span className="ds-company-v2-hero__badge-rule" aria-hidden="true" />
            </span>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="ds-company-v2-hero__title font-display font-kr font-extrabold"
          >
            <span className="ds-company-v2-hero__title-line">
              <span className="ds-company-v2-hero__title-mark">24시간 일하는</span>
            </span>
            <span className="ds-company-v2-hero__title-line">비즈니스 플랫폼,</span>
            <span className="ds-company-v2-hero__title-line">만들고 끝이 아닙니다.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="font-kr mt-8 md:mt-9 text-base md:text-lg lg:text-xl text-white/70 leading-[1.75] max-w-xl [word-break:keep-all]"
          >
            기획부터 AI 자동화까지, 성장에 필요한 모든 것을 담았습니다.
            <br />
            단순한 웹사이트가 아닌 고객을 유입시키고 전환시키는
            <br />
            비즈니스 플랫폼을 구축합니다.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-10 md:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <button
              data-testid="hero-contact-btn"
              onClick={onContact}
              className="ds-company-v2-hero__btn ds-company-v2-hero__btn--primary ds-company-v2-hero__clickable group"
            >
              <span className="ds-company-v2-hero__btn-label">프로젝트 문의하기</span>
              <ArrowRight
                size={17}
                strokeWidth={2.25}
                className="ds-company-v2-hero__btn-arrow"
                aria-hidden="true"
              />
            </button>
            <button
              data-testid="hero-services-btn"
              type="button"
              onClick={scrollToServices}
              className="ds-company-v2-hero__btn ds-company-v2-hero__btn--secondary ds-company-v2-hero__clickable group"
            >
              <span className="ds-company-v2-hero__btn-label">서비스 살펴보기</span>
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
